import type {
  DataObjectValidationError,
  IAttributeTemplatePair,
  IDataObject,
  IDataObjectOrTemplate,
  IDataTemplate,
  ILinkByUid,
} from "../data-object";
import { isLinkUid } from "../data-object";
import type { IFile } from "../file";
import { isOfType } from "../utils";

export interface SaveIngestRequest {
  column_mappings: IColumnMapping[];
  data_objects: IDataObjectOrTemplate[];
  file: IFile;
}

export interface IngestConfigPayload {
  column_mappings: (IColumnMapping | null)[];
  gemd_objects: IDataObjectOrTemplate[];
}

export interface IColumnMappingPointer {
  column_name: string;
  index: number;
}

export type FieldValueTypes =
  | "name"
  | "notes"
  | "description"
  | "label"
  | "tag"
  | "id"
  | "scope"
  | "filename"
  | "file_url"
  | "source.by"
  | "source.date"
  | "origin"
  | "value.nominal"
  | "value.mean"
  | "value.std"
  | "value.lower_bound"
  | "value.upper_bound"
  | "value.units"
  | "value.category"
  | "value.formula"
  | "value.smiles"
  | "value.inchi"
  | "absolute_quantity.units"
  | "absolute_quantity.nominal"
  | "absolute_quantity.mean"
  | "absolute_quantity.std"
  | "absolute_quantity.lower_bound"
  | "absolute_quantity.upper_bound"
  | "mass_fraction.nominal"
  | "mass_fraction.mean"
  | "mass_fraction.std"
  | "mass_fraction.lower_bound"
  | "mass_fraction.upper_bound"
  | "volume_fraction.nominal"
  | "volume_fraction.mean"
  | "volume_fraction.std"
  | "volume_fraction.lower_bound"
  | "volume_fraction.upper_bound"
  | "number_fraction.nominal"
  | "number_fraction.mean"
  | "number_fraction.std"
  | "number_fraction.lower_bound"
  | "number_fraction.upper_bound"
  | "material.id"
  | "material.scope"
  | "process.id"
  | "process.scope"
  | "template.id"
  | "template.scope"
  | "spec.id"
  | "spec.scope";
export interface IField {
  value: FieldValueTypes;
}

export interface IFieldMappingPointer {
  attribute_template_pointer?: AttributeTemplatePointer;
  field: IField;
}

export interface BaseAttributeTemplatePointer {
  id: string;
  scope: string;
  // "Optional: If this is a pointer to a value type, the value type being identified"
  value_type:
    | "nominal_real"
    | "nominal_categorical"
    | "smiles"
    | "inchi"
    | "discrete_categorical"
    | "nominal_composition"
    | "nominal_integer"
    | "uniform_integer"
    | "uniform_real"
    | "normal_real"
    | "empirical_formula";
  // "Optional: If this is a pointer to a Real Value type, the units of that value.";
  units?: string;
}

export type UnitsAttributeTemplatePointer =
  | {
      value_type: "nominal_real";
      units: string;
    }
  | {
      units?: undefined;
    };

export type AttributeTemplatePointer = BaseAttributeTemplatePointer &
  UnitsAttributeTemplatePointer;

export type ColumnMappingType = "column_mapping" | "user_entered";

export interface IColumnMapping {
  type: ColumnMappingType;
  column_name_and_index: IColumnMappingPointer;
  object_pointer?: ILinkByUid;
  field_pointer?: IFieldMappingPointer;
}
export const isColumnMapping = isOfType<IColumnMapping>(
  "column_mapping",
  "user_entered"
);
export interface GemdBuilderReponse {
  columnMappingsByRunId: {
    [key: string]: IColumnMapping[];
  };
  runs: IDataObject[];
  specs: IDataObject[];
  templatesFromBatchGet: IDataTemplate[];
  attributesFromBatchGet: IDataTemplate[];
}

export interface CsvIngestResponse {
  job_id: string;
}

export interface CsvIngestRequestPayload {
  file_version_id: string;
}

export class IngestOperationError extends Error {
  public errorMessages: DataObjectValidationError[];
  constructor(message: string, validation_errors: DataObjectValidationError[]) {
    super(message);
    this.errorMessages = validation_errors;
  }
}

// we can have a list of link by uid's so we just want to know if the id we are comparing exists in that list.
export const isInLinkByUidList = (
  list: ILinkByUid[],
  id: string | undefined | null
) => {
  // if there is no list or is empty, then it cannot be found
  if (list?.length === 0 || !id) {
    return false;
  }
  const match = list.find((link) => link.id === id);
  return !!match;
};

export const getLinkByUidListFromAttributePair = (
  originalList: IDataTemplate[] = [],
  attributes: IAttributeTemplatePair[]
) => {
  return attributes
    .map((attributePair) => attributePair[0])
    .filter(
      (attribute) =>
        attribute &&
        isLinkUid(attribute) &&
        !originalList.some(
          (templateObject) => templateObject.uids.id === attribute.id
        )
    ) as ILinkByUid[];
};
